// Equipment rules the branch page and the Equipment tab lean on. Mirrors of
// org.EffectiveSettings and profile.CarryOver / ValidateBranchUnits; the server
// stays the authority.
import { ApiError } from './api'
import { t, type StaffLanguage } from './staffText'
import type {
  BranchDoc, CounterView, EquipmentDoc, ProfileProblem, ProfileUnitKind, UnitInstance, UnitInstances, UnitSettings,
} from './types'

export const KIND_ORDER: readonly ProfileUnitKind[] = ['service_point', 'kiosk', 'display', 'desk_display', 'feedback']

/** The kinds an equipment profile offers: a feedback tablet's buttons are customer marks,
 *  so with marks off it is offered only where a profile already holds one. */
export function kindsOffered(units: readonly { kind: ProfileUnitKind }[], marks: boolean): ProfileUnitKind[] {
  return KIND_ORDER.filter((k) => k !== 'feedback' || marks || units.some((u) => u.kind === k))
}

export const CODE_PREFIX: Record<ProfileUnitKind, string> = {
  service_point: 'D', kiosk: 'K', display: 'TV', desk_display: 'DD', feedback: 'FB',
}

export const SETTING_FIELDS: Record<ProfileUnitKind, (keyof UnitSettings)[]> = {
  service_point: ['withheld'],
  kiosk: ['printer', 'layoutId', 'ticketLayoutId', 'voice'],
  display: ['layoutId', 'voice', 'callPage'],
  desk_display: ['layoutId', 'voice'],
  feedback: ['voice'],
}

const ALL_FIELDS: (keyof UnitSettings)[] = ['withheld', 'printer', 'layoutId', 'ticketLayoutId', 'voice', 'callPage']
const MAX_INSTANCES = 99

export function codeShapeOk(code: string): boolean {
  return /^[A-Z0-9_-]{1,16}$/.test(code)
}

function upperSet(codes: Iterable<string>): Set<string> {
  const s = new Set<string>()
  for (const c of codes) s.add(c.toUpperCase())
  return s
}

function freeNumber(prefix: string, taken: Set<string>): number {
  let n = 1
  while (taken.has(prefix + n)) n++
  return n
}

/** The whole address a new key opens: the server's (the organisation's own host on the cloud), else this page's origin. */
export function keyedAddress(url: string, path: string, origin: string): string {
  return /^https?:\/\//.test(url) ? url : origin + path
}

/** A desk as a picker lists it: its code, then its name when that says more. */
export function deskLabel(code: string, name?: string): string {
  return name && name.trim() && name.trim().toUpperCase() !== code.toUpperCase() ? `${code} · ${name.trim()}` : code
}

/** A new profile unit's name: "Kiosk", then "Kiosk 2". Instances add their own number. */
export function freeName(doc: EquipmentDoc, base: string): string {
  const taken = new Set(doc.units.map((u) => u.name.trim().toLowerCase()))
  if (!taken.has(base.toLowerCase())) return base
  let n = 2
  while (taken.has(`${base} ${n}`.toLowerCase())) n++
  return `${base} ${n}`
}

export function nextCode(kind: ProfileUnitKind, taken: Iterable<string>): string {
  const prefix = CODE_PREFIX[kind]
  return prefix + freeNumber(prefix, upperSet(taken))
}

/** Raising appends numbers above the highest (then the lowest free past 99); lowering retires the highest numbers.
 *  A new desk display or feedback tablet is linked to the first of desks that no instance in linked stands at. */
export function withCount(
  ui: UnitInstances, kind: ProfileUnitKind, unitName: string, count: number, taken: Iterable<string>,
  link?: { desks: readonly string[]; linked: Iterable<string> },
): { next: UnitInstances; retiring: UnitInstance[] } {
  const want = Math.max(0, Math.min(MAX_INSTANCES, Math.floor(count)))
  const have = ui.instances
  if (want <= have.length) {
    const byNumber = [...have].sort((a, b) => a.number - b.number)
    const keep = new Set(byNumber.slice(0, want))
    return {
      next: { ...ui, instances: have.filter((i) => keep.has(i)) },
      retiring: byNumber.slice(want),
    }
  }
  const used = upperSet(taken)
  for (const i of have) used.add(i.code.toUpperCase())
  const prefix = CODE_PREFIX[kind]
  let number = have.reduce((m, i) => Math.max(m, i.number), 0)
  const numbers = new Set(have.map((i) => i.number))
  const added: UnitInstance[] = []
  const standsAt = new Set<string>()
  for (const d of link?.linked ?? []) standsAt.add(d.toUpperCase())
  for (const i of have) if (i.desk) standsAt.add(i.desk.toUpperCase())
  const linksDesk = kind === 'desk_display' || kind === 'feedback'
  while (have.length + added.length < want) {
    const n = freeNumber(prefix, used)
    used.add(prefix + n)
    let at: number
    if (number < MAX_INSTANCES) at = ++number
    // Past the range, the lowest number no instance holds.
    else for (at = 1; numbers.has(at); at++);
    numbers.add(at)
    const inst: UnitInstance = { number: at, code: prefix + n, name: `${unitName} ${n}`, overrides: {}, sortOrder: at }
    const desk = linksDesk ? link?.desks.find((d) => !standsAt.has(d.toUpperCase())) : undefined
    if (desk) {
      inst.desk = desk
      standsAt.add(desk.toUpperCase())
    }
    added.push(inst)
  }
  return { next: { ...ui, instances: [...have, ...added] }, retiring: [] }
}

/** Each field whole from the narrowest level that sets it. */
export function effective(defaults: UnitSettings, profile: UnitSettings, own: UnitSettings): UnitSettings {
  const out: Record<string, unknown> = {}
  for (const f of ALL_FIELDS) {
    const v = own[f] ?? profile[f] ?? defaults[f]
    if (v != null) out[f] = v
  }
  return out as UnitSettings
}

export function sourceOf(field: keyof UnitSettings, profile: UnitSettings, own: UnitSettings): 'own' | 'profile' | 'default' {
  if (own[field] != null) return 'own'
  if (profile[field] != null) return 'profile'
  return 'default'
}

export interface CarryResult {
  next: BranchDoc
  kept: number
  created: number
  dropped: UnitInstance[]
  /** Kiosks whose own design is of another operation profile, cleared (KIOSK_DESIGN_CLEARED). */
  clearedDesigns: string[]
  /** Desk displays and tablets not made for want of a desk to link (INSTANCES_NOT_MADE). */
  notMade: { unit: string; count: number }[]
}

/**
 * §11.4, mirroring the server's draft save (profile.CarryOver, then
 * ClearKioskDesigns): groups already under the target pass through; others move to
 * the first profile unit of their kind, spilling past 99; a kind the branch has no
 * instances of gets its default count, desks first, and each new desk display or
 * tablet is linked to a desk in order, none made once the desks run out. A kiosk's
 * own design not of the target operation profile is cleared; kioskDesigns are that
 * profile's kiosk designs, and when they are unknown a change of operation profile
 * clears every kiosk's own design (a kiosk design belongs to one profile).
 */
export function carryOver(
  draft: BranchDoc,
  from: EquipmentDoc,
  to: { operationProfileId: string; equipmentProfileId: string; document: EquipmentDoc; kioskDesigns?: ReadonlySet<string> },
): CarryResult {
  const toUnits = to.document.units
  const inTo = new Map<string, ProfileUnitKind>()
  for (const pu of toUnits) if (!inTo.has(pu.id)) inTo.set(pu.id, pu.kind)
  const carried = draft.units.length === 0 || draft.units.some((g) => !inTo.has(g.profileUnitId))
  const opChanged = to.operationProfileId !== draft.operationProfileId

  let units: UnitInstances[] = draft.units
  let kept = 0
  let created = 0
  const dropped: UnitInstance[] = []
  const notMade: { unit: string; count: number }[] = []

  if (!carried) {
    kept = draft.units.reduce((n, g) => n + g.instances.length, 0)
  } else {
    const kindOf = new Map(from.units.map((u) => [u.id, u.kind]))
    const toKinds = new Set(toUnits.map((u) => u.kind))
    const keptBy = new Map<string, UnitInstance[]>()
    const skip = new Set<ProfileUnitKind>()
    const old = new Map<ProfileUnitKind, UnitInstance[]>()
    for (const g of draft.units) {
      const here = inTo.get(g.profileUnitId)
      if (here) {
        keptBy.set(g.profileUnitId, [...(keptBy.get(g.profileUnitId) ?? []), ...g.instances])
        skip.add(here)
        continue
      }
      const k = kindOf.get(g.profileUnitId)
      if (!k || !toKinds.has(k)) {
        dropped.push(...g.instances)
        continue
      }
      old.set(k, [...(old.get(k) ?? []), ...g.instances])
      if (g.instances.length) skip.add(k)
    }

    const copy = (i: UnitInstance): UnitInstance => {
      const c: UnitInstance = { ...i, overrides: { ...i.overrides } }
      if (i.deskFilter) c.deskFilter = [...i.deskFilter]
      return c
    }
    const used = new Set<string>()
    const lastOf = new Map<ProfileUnitKind, number>()
    units = toUnits.map((pu, i) => {
      const instances = (keptBy.get(pu.id) ?? []).map(copy)
      keptBy.delete(pu.id)
      for (const x of instances) used.add(x.code.toUpperCase())
      lastOf.set(pu.kind, i)
      return { profileUnitId: pu.id, instances }
    })
    toUnits.forEach((pu, i) => {
      const queue = old.get(pu.kind) ?? []
      const group = units[i]!
      while (queue.length && (group.instances.length < MAX_INSTANCES || lastOf.get(pu.kind) === i)) {
        const inst = copy(queue.shift()!)
        inst.number = group.instances.reduce((m, x) => Math.max(m, x.number + 1), 1)
        group.instances.push(inst)
        used.add(inst.code.toUpperCase())
      }
    })
    kept = units.reduce((n, g) => n + g.instances.length, 0)

    const make = (i: number, desk?: () => string | undefined) => {
      const pu = toUnits[i]!
      const prefix = CODE_PREFIX[pu.kind]
      for (let n = 1; n <= pu.defaultCount; n++) {
        let linked: string | undefined
        if (desk) {
          linked = desk()
          if (linked === undefined) {
            notMade.push({ unit: pu.name, count: pu.defaultCount - n + 1 })
            return
          }
        }
        const num = freeNumber(prefix, used)
        used.add(prefix + num)
        const inst: UnitInstance = { number: n, code: prefix + num, name: `${pu.name} ${num}`, overrides: {}, sortOrder: n }
        if (linked !== undefined) inst.desk = linked
        units[i]!.instances.push(inst)
        created++
      }
    }
    toUnits.forEach((pu, i) => {
      if (pu.kind === 'service_point' && !skip.has(pu.kind)) make(i)
    })
    const desks = units.flatMap((g, i) => (toUnits[i]!.kind === 'service_point' ? g.instances.map((x) => x.code) : []))
    const cursor = new Map<ProfileUnitKind, number>()
    toUnits.forEach((pu, i) => {
      if (pu.kind === 'service_point' || skip.has(pu.kind)) return
      const linksDesk = pu.kind === 'desk_display' || pu.kind === 'feedback'
      make(
        i,
        linksDesk
          ? () => {
              const at = cursor.get(pu.kind) ?? 0
              if (at >= desks.length) return undefined
              cursor.set(pu.kind, at + 1)
              return desks[at]
            }
          : undefined,
      )
    })
  }

  const clearedDesigns: string[] = []
  if (opChanged || carried) {
    const mayKeep = (id: string) => (to.kioskDesigns ? to.kioskDesigns.has(id) : !opChanged)
    units = units.map((g) => {
      if (inTo.get(g.profileUnitId) !== 'kiosk') return g
      return {
        ...g,
        instances: g.instances.map((x) => {
          const id = x.overrides.layoutId
          if (!id || mayKeep(id)) return x
          clearedDesigns.push(x.code.toUpperCase())
          const overrides = { ...x.overrides }
          delete overrides.layoutId
          return { ...x, overrides }
        }),
      }
    })
  }

  return {
    next: { ...draft, operationProfileId: to.operationProfileId, equipmentProfileId: to.equipmentProfileId, units },
    kept, created, dropped, clearedDesigns, notMade,
  }
}

/** Renames a service point's code and every desk link and desk filter naming it. */
export function renameDeskCode(doc: BranchDoc, from: string, to: string): BranchDoc {
  const key = from.toUpperCase()
  if (!key) return doc
  const is = (c: string | undefined) => c !== undefined && c.toUpperCase() === key
  return {
    ...doc,
    units: doc.units.map((g) => ({
      ...g,
      instances: g.instances.map((i) => {
        const out = { ...i }
        if (is(i.code)) out.code = to
        if (is(i.desk)) out.desk = to
        if (i.deskFilter) out.deskFilter = i.deskFilter.map((c) => (is(c) ? to : c))
        return out
      }),
    })),
  }
}

/** Instances a lowered count retires, with whoever is signed in at a retiring desk. */
export function retiring(removed: UnitInstance[], kind: ProfileUnitKind, live: CounterView[]): { code: string; staffName?: string }[] {
  return removed.map((i) => {
    if (kind !== 'service_point') return { code: i.code }
    const c = live.find((v) => v.code.toUpperCase() === i.code.toUpperCase())
    return c?.staffName ? { code: i.code, staffName: c.staffName } : { code: i.code }
  })
}

/**
 * The instance checks the server makes on a draft save, at the same paths and
 * codes. A duplicate code is pinned on every holder; instances of a profile unit
 * the equipment profile no longer has are left to the server's warning.
 */
export function localProblems(doc: BranchDoc, equipment: EquipmentDoc, lang: StaffLanguage = 'en'): ProfileProblem[] {
  const kindOf = new Map<string, ProfileUnitKind>()
  for (const u of equipment.units) if (!kindOf.has(u.id)) kindOf.set(u.id, u.kind)

  const desks = new Set<string>()
  const codeCount = new Map<string, number>()
  for (const g of doc.units) {
    const k = kindOf.get(g.profileUnitId)
    if (!k) continue
    for (const i of g.instances) {
      const c = i.code.toUpperCase()
      codeCount.set(c, (codeCount.get(c) ?? 0) + 1)
      if (k === 'service_point') desks.add(c)
    }
  }

  const ps: ProfileProblem[] = []
  doc.units.forEach((g, gi) => {
    const kind = kindOf.get(g.profileUnitId)
    if (!kind) return
    g.instances.forEach((i, j) => {
      const at = `units[${gi}].instances[${j}]`
      const code = i.code.toUpperCase()
      if (!codeShapeOk(code)) {
        ps.push({ path: `${at}.code`, code: 'INSTANCE_CODE_FORMAT', message: t('eq.problem.codeFormat', lang), args: { code: i.code } })
      } else if ((codeCount.get(code) ?? 0) > 1) {
        ps.push({ path: `${at}.code`, code: 'INSTANCE_CODE_TAKEN', message: t('eq.problem.codeTaken', lang), args: { code } })
      }
      if (!i.name.trim()) ps.push({ path: `${at}.name`, code: 'NAME_MISSING', message: t('eq.problem.nameMissing', lang) })
      if (kind === 'desk_display' || kind === 'feedback') {
        if (!i.desk) ps.push({ path: `${at}.desk`, code: 'DESK_MISSING', message: t('eq.problem.deskMissing', lang) })
        else if (!desks.has(i.desk.toUpperCase())) {
          ps.push({ path: `${at}.desk`, code: 'DESK_UNKNOWN', message: t('eq.problem.deskUnknown', lang), args: { code: i.desk } })
        }
      }
      if (kind === 'display') {
        const seen = new Set<string>()
        ;(i.deskFilter ?? []).forEach((d, k) => {
          const up = d.toUpperCase()
          if (!desks.has(up)) {
            ps.push({ path: `${at}.deskFilter[${k}]`, code: 'DESK_FILTER_UNKNOWN', message: t('eq.problem.deskUnknown', lang), args: { code: d } })
          } else if (seen.has(up)) {
            ps.push({ path: `${at}.deskFilter[${k}]`, code: 'DESK_FILTER_REPEATED', message: t('eq.problem.deskRepeated', lang), args: { code: up } })
          }
          seen.add(up)
        })
      }
    })
  })
  return ps
}

/** Problems at path prefix or under it ("units[1]" does not take "units[10]"). */
export function problemsAt(problems: ProfileProblem[], prefix: string): ProfileProblem[] {
  return problems.filter((p) => p.path === prefix || p.path.startsWith(prefix + '.') || p.path.startsWith(prefix + '['))
}

export function unitIndexOf(path: string): number | null {
  const m = /^units\[(\d+)\]/.exec(path)
  return m ? Number(m[1]) : null
}

export function instanceIndexOf(path: string): [number, number] | null {
  const m = /^units\[(\d+)\]\.instances\[(\d+)\]/.exec(path)
  return m ? [Number(m[1]), Number(m[2])] : null
}

export function saveOutcome(err: unknown): { kind: 'stale' } | { kind: 'problems'; problems: ProfileProblem[] } | { kind: 'other' } {
  if (!(err instanceof ApiError)) return { kind: 'other' }
  if (err.status === 409 && err.code === 'EQUIPMENT_STALE') return { kind: 'stale' }
  if (err.status === 422 && err.problems?.length) return { kind: 'problems', problems: err.problems }
  return { kind: 'other' }
}
